import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';

interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  language: string;
  timezone: string;
  notificationEmail: boolean;
  notificationBrowser: boolean;
  dataRetentionDays: number;
}

export function usePreferences() {
  const queryClient = useQueryClient();

  const preferencesQuery = useQuery({
    queryKey: ['preferences'],
    queryFn: async () => {
      return await apiClient<UserPreferences>('/api/users/preferences');
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Remove enabled check to prevent Safari timing issues
    // The apiClient will handle authentication errors instead
  });

  const updatePreferences = useMutation({
    mutationFn: async (preferences: Partial<UserPreferences>) => {
      await apiClient('/api/users/preferences', {
        method: 'PUT',
        body: JSON.stringify(preferences),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preferences'] });
    },
  });

  return {
    preferences: preferencesQuery.data,
    isLoading: preferencesQuery.isLoading,
    error: preferencesQuery.error,
    updatePreferences: updatePreferences.mutate,
    isUpdating: updatePreferences.isPending,
  };
}
